import React, { useState } from 'react';
import { Heart, MessageCircle, Share2, Bookmark, TrendingUp, Calendar, User, ArrowRight, Eye } from 'lucide-react';
import './smfblog.css';
import Navbar from '../../Navbar/nbar'

const FinanceBlog = () => {
  const [reactions, setReactions] = useState({
    likes: 247,
    comments: 18,
    shares: 42
  });
  const [userReacted, setUserReacted] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [currentAction, setCurrentAction] = useState(''); // 'subscribe' or 'follow'
  const [verificationStep, setVerificationStep] = useState('email'); // 'email' or 'otp'
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success', 'error', 'info'
  const [isLoading, setIsLoading] = useState(false);
  const [showSubscribeSuccess, setShowSubscribeSuccess] = useState(false);
  const [showFollowSuccess, setShowFollowSuccess] = useState(false);
  
  // Simulated database of existing emails
  const [existingEmails, setExistingEmails] = useState(new Set(['john@example.com', 'sarah@company.com', 'mike@finance.com']));
  
  const [comments, setComments] = useState([
    {
      id: 1,
      author: "Sarah Chen",
      role: "Portfolio Manager",
      content: "Excellent analysis on market volatility. The data-driven approach really resonates with our investment philosophy.",
      time: "2 hours ago",
      likes: 12
    },
    {
      id: 2,
      author: "Michael Rodriguez",
      role: "Risk Analyst",
      content: "The section on emerging markets provides valuable insights. Would love to see more on ESG integration.",
      time: "4 hours ago",
      likes: 8
    }
  ]);
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState(false);

  const handleReaction = (type) => {
    if (type === 'like') {
      setReactions(prev => ({
        ...prev,
        likes: userReacted ? prev.likes - 1 : prev.likes + 1
      }));
      setUserReacted(!userReacted);
    } else {
      setReactions(prev => ({
        ...prev,
        [type]: prev[type] + 1
      }));
    }
  };

  const handleAddComment = () => {
    if (newComment.trim()) {
      const comment = {
        id: comments.length + 1,
        author: "You",
        role: "Reader",
        content: newComment,
        time: "Just now",
        likes: 0
      };
      setComments([comment, ...comments]);
      setNewComment('');
      setReactions(prev => ({ ...prev, comments: prev.comments + 1 }));
    }
  };

  const generateOtp = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 4000);
  };

  const handleEmailSubmit = async () => {
    if (!email || !email.includes('@')) {
      showMessage('Please enter a valid email address', 'error');
      return;
    }

    if (existingEmails.has(email.toLowerCase())) {
      showMessage('This email is already registered. Please use a different email.', 'error');
      return;
    }

    setIsLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      const newOtp = generateOtp();
      setGeneratedOtp(newOtp);
      setVerificationStep('otp');
      setIsLoading(false);
      showMessage(`OTP sent to ${email}. Use: ${newOtp} (Demo)`, 'info');
    }, 1500);
  };

  const handleOtpVerification = () => {
    if (!otp) {
      showMessage('Please enter the OTP', 'error');
      return;
    }

    if (otp !== generatedOtp) {
      showMessage('Invalid OTP. Please try again.', 'error');
      return;
    }

    // Add email to existing emails set
    setExistingEmails(prev => new Set([...prev, email.toLowerCase()]));

    if (currentAction === 'subscribe') {
      setIsSubscribed(true);
      setShowSubscribeSuccess(true);
      showMessage('Successfully subscribed to Weekly Market Brief!', 'success');
      setTimeout(() => setShowSubscribeSuccess(false), 3000);
    } else if (currentAction === 'follow') {
      setIsFollowing(true);
      setShowFollowSuccess(true);
      showMessage('Now following Dr. James Patterson!', 'success');
      setTimeout(() => setShowFollowSuccess(false), 3000);
    }

    // Reset modal
    setShowOtpModal(false);
    setVerificationStep('email');
    setEmail('');
    setOtp('');
    setGeneratedOtp('');
    setCurrentAction('');
  };

  const handleFollow = () => {
    if (isFollowing) {
      setIsFollowing(false);
      showMessage('Unfollowed Dr. James Patterson', 'info');
      return;
    }
    
    setCurrentAction('follow');
    setShowOtpModal(true);
    setVerificationStep('email');
  };

  const handleSubscribe = () => {
    if (isSubscribed) {
      showMessage('You are already subscribed', 'info');
      return;
    }
    
    setCurrentAction('subscribe');
    setShowOtpModal(true);
    setVerificationStep('email');
  };

  const handleHeaderSubscribe = () => {
    handleSubscribe();
  };

  const handleSidebarSubscribe = () => {
    if (isSubscribed) {
      showMessage('You are already subscribed', 'info');
      return;
    }
    
    setCurrentAction('subscribe');
    setShowOtpModal(true);
    setVerificationStep('email');
  };

  const closeModal = () => {
    setShowOtpModal(false);
    setVerificationStep('email');
    setEmail('');
    setOtp('');
    setGeneratedOtp('');
    setCurrentAction('');
    setIsLoading(false);
  };

  return (
    <div className="finance-blog">
      {/* Header */}
      <Navbar />
      

      {/* Success Notification */}
      {message && (
        <div className={`notification ${messageType}`}>
          <div className="notification-content">
            <span className="notification-icon">
              {messageType === 'success' ? '✓' : messageType === 'error' ? '✕' : 'ℹ'}
            </span>
            <span className="notification-text">{message}</span>
          </div>
        </div>
      )}

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <div className="modal-icon">
                {currentAction === 'subscribe' ? (
                  <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                ) : (
                  <User className="icon-svg" />
                )}
              </div>
              <h2 className="modal-title">
                {currentAction === 'subscribe' ? 'Subscribe to Newsletter' : 'Follow Dr. James Patterson'}
              </h2>
              <p className="modal-description">
                {verificationStep === 'email' 
                  ? 'Enter your email to receive verification code'
                  : `Enter the 6-digit code sent to ${email}`
                }
              </p>
            </div>

            {verificationStep === 'email' ? (
              <div className="modal-form">
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="form-input"
                    disabled={isLoading}
                  />
                </div>
                <div className="modal-buttons">
                  <button
                    onClick={closeModal}
                    className="btn-secondary"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEmailSubmit}
                    disabled={isLoading}
                    className="btn-primary"
                  >
                    {isLoading ? 'Sending...' : 'Send OTP'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="modal-form">
                <div className="form-group">
                  <label className="form-label">Verification Code</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    className="otp-input"
                    maxLength={6}
                  />
                  <p className="email-confirmation">
                    Code sent to: <span className="email-highlight">{email}</span>
                  </p>
                </div>
                <div className="modal-buttons">
                  <button
                    onClick={() => {
                      setVerificationStep('email');
                      setOtp('');
                    }}
                    className="btn-secondary"
                  >
                    Change Email
                  </button>
                  <button
                    onClick={handleOtpVerification}
                    className="btn-primary"
                  >
                    Verify & {currentAction === 'subscribe' ? 'Subscribe' : 'Follow'}
                  </button>
                </div>
                <button
                  onClick={() => {
                    const newOtp = generateOtp();
                    setGeneratedOtp(newOtp);
                    showMessage(`New OTP sent to ${email}. Use: ${newOtp} (Demo)`, 'info');
                  }}
                  className="resend-btn"
                >
                  Resend OTP
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="main-container">
        <div className="content-grid">
          {/* Main Content */}
          <div className="main-content">
            <article className="article-card">
              {/* Article Header */}
              <div className="article-header">
                <div className="article-overlay"></div>
                <div className="article-meta">
                  <div className="meta-badges">
                    <span className="category-badge">Market Analysis</span>
                    <span className="views-badge">
                      <Eye className="icon-sm" />
                      2.4k views
                    </span>
                  </div>
                  <h1 className="article-title">
                    Navigating Market Volatility in 2025: A Data-Driven Approach
                  </h1>
                  <div className="article-info">
                    <div className="info-item">
                      <Calendar className="icon-sm" />
                      March 15, 2025
                    </div>
                    <div className="info-item">
                      <User className="icon-sm" />
                      Dr. James Patterson
                    </div>
                    <span>8 min read</span>
                  </div>
                </div>
              </div>

              {/* Article Content */}
              <div className="article-body">
                <div className="prose">
                  <div className="article-summary">
                    As we progress through 2025, institutional investors face unprecedented market conditions. Our research team has analyzed over 10,000 data points to identify key patterns and strategic opportunities.
                  </div>

                  <h2 className="section-title">Executive Summary</h2>
                  <p className="content-text">
                    The current market environment presents both challenges and opportunities for sophisticated investors. Our analysis reveals three critical factors driving market behavior: technological disruption, geopolitical tensions, and evolving monetary policy frameworks.
                  </p>

                  <div className="highlight-box">
                    <div className="highlight-content">
                      <TrendingUp className="highlight-icon" />
                      <div>
                        <h3 className="highlight-title">Key Finding</h3>
                        <p className="highlight-text">
                          Markets showing 23% higher volatility compared to historical averages, presenting selective opportunities for value creation through disciplined risk management.
                        </p>
                      </div>
                    </div>
                  </div>

                  <h2 className="section-title">Market Dynamics Analysis</h2>
                  <p className="content-text">
                    Our quantitative models indicate that traditional correlation patterns have shifted significantly. The relationship between equity and fixed-income markets has evolved, requiring adaptive allocation strategies.
                  </p>

                  <div className="metrics-grid">
                    <div className="metric-card">
                      <h3 className="metric-title">Equity Markets</h3>
                      <p className="metric-value positive">+12.3%</p>
                      <p className="metric-label">YTD Performance</p>
                    </div>
                    <div className="metric-card">
                      <h3 className="metric-title">Fixed Income</h3>
                      <p className="metric-value negative">-2.1%</p>
                      <p className="metric-label">YTD Performance</p>
                    </div>
                    <div className="metric-card">
                      <h3 className="metric-title">Alternatives</h3>
                      <p className="metric-value purple">+8.7%</p>
                      <p className="metric-label">YTD Performance</p>
                    </div>
                  </div>

                  <h2 className="section-title">Strategic Implications</h2>
                  <p className="content-text">
                    Based on our analysis, we recommend a multi-faceted approach that balances risk and opportunity. The key is maintaining discipline while remaining adaptable to changing market conditions.
                  </p>

                  <div className="quote-box">
                    <h3 className="quote-title">Management Philosophy</h3>
                    <p className="quote-text">
                      "In times of uncertainty, the greatest risk is not taking calculated risks. Our data-driven approach enables us to identify opportunities where others see only volatility."
                    </p>
                    <p className="quote-attribution">- Ray Dalio-inspired principle</p>
                  </div>
                </div>

                {/* Reaction Bar */}
                <div className="reaction-bar">
                  <div className="reaction-buttons">
                    <button
                      onClick={() => handleReaction('like')}
                      className={`reaction-btn ${userReacted ? 'liked' : ''}`}
                    >
                      <Heart className={`icon-sm ${userReacted ? 'filled' : ''}`} />
                      <span className="reaction-count">{reactions.likes}</span>
                    </button>
                    <button
                      onClick={() => setShowComments(!showComments)}
                      className="reaction-btn"
                    >
                      <MessageCircle className="icon-sm" />
                      <span className="reaction-count">{reactions.comments}</span>
                    </button>
                    <button
                      onClick={() => handleReaction('shares')}
                      className="reaction-btn"
                    >
                      <Share2 className="icon-sm" />
                      <span className="reaction-count">{reactions.shares}</span>
                    </button>
                  </div>
                  <button className="save-btn">
                    <Bookmark className="icon-sm" />
                    <span className="reaction-count">Save</span>
                  </button>
                </div>
              </div>
            </article>

            {/* Comments Section */}
            {showComments && (
              <div className="comments-section">
                <h3 className="comments-title">Comments ({reactions.comments})</h3>
                
                {/* Add Comment */}
                <div className="add-comment">
                  <div className="comment-form">
                    <div className="comment-avatar">
                      <User className="avatar-icon" />
                    </div>
                    <div className="comment-input-container">
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Share your professional insights..."
                        className="comment-textarea"
                        rows="3"
                      />
                      <div className="comment-actions">
                        <button
                          onClick={handleAddComment}
                          className="post-comment-btn"
                        >
                          Post Comment
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Comments List */}
                <div className="comments-list">
                  {comments.map(comment => (
                    <div key={comment.id} className="comment-item">
                      <div className="comment-avatar">
                        <User className="avatar-icon" />
                      </div>
                      <div className="comment-content">
                        <div className="comment-bubble">
                          <div className="comment-header">
                            <span className="comment-author">{comment.author}</span>
                            <span className="comment-role">{comment.role}</span>
                            <span className="comment-dot">•</span>
                            <span className="comment-time">{comment.time}</span>
                          </div>
                          <p className="comment-text">{comment.content}</p>
                        </div>
                        <div className="comment-actions">
                          <button className="comment-like-btn">
                            <Heart className="icon-xs" />
                            <span className="like-count">{comment.likes}</span>
                          </button>
                          <button className="comment-reply-btn">Reply</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="sidebar">
            {/* Author Info */}
            <div className="sidebar-card">
              <div className="author-profile">
                <div className="author-avatar">
                  <User className="avatar-icon-lg" />
                </div>
                <h3 className="author-name">Om Prakash Tiwari</h3>
                <p className="author-title">Chief Financial Officer</p>
                <p className="author-bio">
                2+ years experience in Financial literacy Components.
                </p>
                <button 
                  onClick={handleFollow}
                  className={`follow-btn ${isFollowing ? 'following' : ''}`}
                >
                  {isFollowing ? 'Following ✓' : 'Follow'}
                </button>
                {showFollowSuccess && (
                  <div className="follow-success">
                    {isFollowing ? 'Now following Dr. James Patterson!' : 'Unfollowed Dr. James Patterson'}
                  </div>
                )}
              </div>
            </div>

            {/* Related Articles */}
            <div className="sidebar-card">
              <h3 className="sidebar-title">Related Insights</h3>
              <div className="related-articles">
                <div className="related-item">
                  <h4 className="related-title">
                    Alternative Investments in Rising Rate Environment
                  </h4>
                  <p className="related-date">March 10, 2025</p>
                </div>
                <div className="related-item">
                  <h4 className="related-title">
                    ESG Integration: Beyond Compliance
                  </h4>
                  <p className="related-date">March 8, 2025</p>
                </div>
                <div className="related-item">
                  <h4 className="related-title">
                    Global Macro Trends for Institutional Needs
                  </h4>
                  <p className="related-date">March 5, 2025</p>
                </div>
              </div>
              <button className="view-all-btn">
                View All Insights
                <ArrowRight className="icon-sm" />
              </button>
            </div>

            {/* Newsletter Signup */}
            <div className="newsletter-card">
              <h3 className="newsletter-title">Weekly Market Brief</h3>
              <p className="newsletter-description">
                Get our institutional-grade analysis delivered to your inbox.
              </p>
              <input
                type="email"
                value=""
                onChange={() => {}}
                placeholder={isSubscribed ? "Already subscribed ✓" : "Click Subscribe to get started"}
                className={`newsletter-input ${isSubscribed ? 'subscribed' : ''}`}
                onClick={handleSidebarSubscribe}
                readOnly
              />
              <button 
                onClick={handleSidebarSubscribe}
                className={`newsletter-btn ${isSubscribed ? 'subscribed' : ''}`}
              >
                {isSubscribed ? 'Subscribed ✓' : 'Subscribe'}
              </button>
              {showSubscribeSuccess && (
                <div className="subscribe-success">
                  Successfully subscribed to Weekly Market Brief!
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-section">
              <h3 className="footer-title">SOMFIN</h3>
              <p className="footer-description">
                Institutional investment management driven by rigorous analysis and principled decision-making.
              </p>
            </div>
            <div className="footer-section">
              <h4 className="footer-heading">Research</h4>
              <div className="footer-links">
                <div className="footer-link">Market Analysis</div>
                <div className="footer-link">Management  Strategy</div>
                <div className="footer-link">Risk Management</div>
                <div className="footer-link">Economic Outlook</div>
              </div>
            </div>
            <div className="footer-section">
              <h4 className="footer-heading">Company</h4>
              <div className="footer-links">
                <div className="footer-link">About Us</div>
                <div className="footer-link">Core Team</div>
                <div className="footer-link">Careers</div>
                <div className="footer-link">Contact</div>
              </div>
            </div>
            <div className="footer-section">
              <h4 className="footer-heading">Legal</h4>
              <div className="footer-links">
                <div className="footer-link">Privacy Policy</div>
                <div className="footer-link">Terms of Service</div>
                <div className="footer-link">Disclosures</div>
                <div className="footer-link">Compliance</div>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 SOMFIN Private Limited. All rights reserved. | Financial Literacy is subject to educational resource.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FinanceBlog;